"use client"

import { useCallback } from "react"

/**
 * Smooth-scrolls to an in-page anchor inside the `#mlp-root` scroll container.
 *
 * Landing pages render inside `#mlp-root`, a `position: fixed` scroll container.
 * A plain `href="#section"` makes the browser jump instantly inside that container,
 * so anchor links have to be intercepted and scrolled manually.
 *
 * Returns a click handler; non-anchor hrefs are left alone so external/absolute
 * links keep their default navigation.
 */
export function useAnchorScroll(onNavigate?: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLElement>, href?: string | null) => {
      if (!href?.startsWith("#")) return

      e.preventDefault()
      // Attribute selector rather than an id selector: sectionId is free text in the
      // CMS, and values like "2-form" or "lead.form" are not valid CSS identifiers.
      const id = CSS.escape(href.slice(1))
      const target = document.querySelector(`#mlp-root [id="${id}"]`)
      target?.scrollIntoView({ behavior: "smooth", block: "start" })
      onNavigate?.()
    },
    [onNavigate],
  )
}

export default useAnchorScroll
